/**
 * Bulk-import batch detail view.
 *
 * A batch wraps N `imported_documents` rows under one progress + results
 * UI. The matching upload entry point lives on `/contracts/import` (see
 * `ImportLanding`); this page is the share-able, refreshable result
 * board for one specific batch.
 *
 * Layout:
 *   - Progress hero: chunky counts + a segmented progress bar.
 *   - Optional "all failed" banner when 100 % of the batch errored — gives
 *     a one-click bulk retry / discard for the infra-down case.
 *   - File cards grid grouped by outcome:
 *       Klart att skapa  → template select + per-row Skapa + bulk "Skapa alla"
 *       Misslyckades     → inline [Försök igen] + [Exkludera] + bulk retry-all.
 *                          "Exkludera" soft-deletes the row from this batch
 *                          (it doesn't produce a contract); contrast with
 *                          "Skapa" which DOES create one. Failures are
 *                          retryable (failed_extract).
 *       Skannade bilder  → non-retryable failure_ocr_required; only
 *                          [Exkludera] makes sense.
 *       Skapade avtal    → open the resulting contract editor
 *   - Pending uploads (status: queued/extracting) surface as cards too
 *     so the user sees the overall fan-out.
 *   - Footer toolbar: "Färdig – avsluta import" (navigate away leaving
 *     any pending rows behind; disabled until at least one contract has
 *     been created) and "Kasta hela importen" (batch_discard).
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getTemplateVersion } from '@hyranai/regta-templates';
import { BellRing, BellOff } from 'lucide-react';
import {
  tenantApi,
  type ContractNotification,
  type ImportBatchDetail,
  type ImportBatchItem,
  type ImportStatus,
  type NotificationKind,
  type TemplateListRow,
} from '../api.js';
import { useTenantSession } from '../auth-context.js';
import { useInvalidate, useTenantQuery } from '../query.js';
import { FileTypeIcon } from '../components/FileTypeIcon.js';
import { usePageTitle } from '../use-page-title.js';

export function BulkImportContractsPage() {
  const { batchId } = useParams<{ batchId: string }>();
  if (!batchId) {
    // Route shouldn't be reachable without a :batchId — the entry point
    // is `/contracts/import`. Render a minimal fallback just in case.
    return (
      <div className="rounded border border-stone-200 bg-stone-50 p-4 text-sm text-stone-600">
        Ingen import vald.{' '}
        <Link to="/contracts/import" className="font-medium underline">
          Starta en import
        </Link>
        .
      </div>
    );
  }
  return <BatchView batchId={batchId} />;
}

function BatchView({ batchId }: { batchId: string }) {
  const { token } = useTenantSession();
  const navigate = useNavigate();
  const invalidate = useInvalidate();

  const detailQ = useTenantQuery(
    'imports.batch_get',
    (id) => tenantApi.imports.batch_get(id as string),
    [batchId],
    { refreshInterval: 2000, revalidateOnFocus: false },
  );
  const detail: ImportBatchDetail | null = (detailQ.data as ImportBatchDetail | undefined) ?? null;

  usePageTitle(detail?.label ? `Bulkimport · ${detail.label}` : 'Bulkimport');

  const templatesQ = useTenantQuery(
    'templates.list',
    () => tenantApi.templates.list(),
  );
  const templates = templatesQ.data?.templates ?? [];

  if (detailQ.error && !detail) {
    return (
      <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
        {(detailQ.error as Error).message}
      </div>
    );
  }
  if (!detail) {
    return <div className="text-sm text-stone-400">loading…</div>;
  }

  const counts = countByStatus(detail);
  const ready = detail.items.filter(isReadyToConfirm);
  const inProgress = detail.items.filter(isInProgress);
  const failed = detail.items.filter(isFailedExtract);
  const ocrRequired = detail.items.filter((i) => i.status === 'failed_ocr_required');
  const confirmed = detail.items.filter((i) => i.status === 'confirmed');
  const allFailed = detail.items.length > 0 && failed.length === detail.items.length;

  const refresh = async () => {
    await detailQ.mutate();
    await invalidate('contracts.list');
    await invalidate('imports.list');
    await invalidate('imports.batch_list');
  };

  return (
    <section className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-stone-800">
            Bulkimport {detail.label ? `· ${detail.label}` : ''}
          </h2>
          <p className="text-xs text-stone-500">
            {detail.total_count} fil{detail.total_count === 1 ? '' : 'er'} · startad {formatDate(detail.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/contracts/import"
            className="rounded border border-stone-300 bg-white px-3 py-1.5 text-xs hover:bg-stone-50"
          >
            + Ny import
          </Link>
        </div>
      </header>

      <ProgressHero detail={detail} counts={counts} />

      {allFailed && (
        <AllFailedBanner
          batchId={detail.id}
          failedCount={failed.length}
          onAfterChange={refresh}
          onDiscarded={() => navigate('/contracts')}
        />
      )}

      {ready.length > 0 && (
        <ReadySection
          items={ready}
          templates={templates}
          onAfterConfirm={refresh}
        />
      )}

      {inProgress.length > 0 && (
        <ResultsSection
          title="Läser dokument"
          subtitle={`Vi extraherar fält från ${inProgress.length} dokument — uppdaterar automatiskt.`}
          tone="info"
        >
          <FileCardGrid>
            {inProgress.map((item) => (
              <InProgressCard key={item.id} item={item} />
            ))}
          </FileCardGrid>
        </ResultsSection>
      )}

      {failed.length > 0 && (
        <FailedSection items={failed} onAfterChange={refresh} />
      )}

      {ocrRequired.length > 0 && (
        <OcrRequiredSection items={ocrRequired} onAfterChange={refresh} />
      )}

      {confirmed.length > 0 && (
        <ResultsSection
          title="Skapade avtal"
          subtitle="Färdiga avtal från denna import."
          tone="muted"
          count={confirmed.length}
        >
          <FileCardGrid>
            {confirmed.map((item) => (
              <ConfirmedCard key={item.id} item={item} />
            ))}
          </FileCardGrid>
        </ResultsSection>
      )}

      {detail.items.length < detail.total_count && (
        <div className="rounded border border-stone-200 bg-stone-50 p-3 text-xs text-stone-500">
          {detail.items.length} av {detail.total_count} filer registrerade hittills.
          Om uppladdningen avbröts, starta en ny import för de filer som saknas.
        </div>
      )}

      <BatchFooterActions
        batchId={detail.id}
        confirmedCount={confirmed.length}
        pendingCount={ready.length + inProgress.length + failed.length + ocrRequired.length}
        onDone={() => navigate('/contracts')}
      />
    </section>
  );
}

function ProgressHero({
  detail,
  counts,
}: {
  detail: ImportBatchDetail;
  counts: ReturnType<typeof countByStatus>;
}) {
  const total = detail.total_count;
  const done = counts.extracted + counts.confirmed + counts.failed;
  const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0);
  const okPct = pct(counts.extracted + counts.confirmed);
  const failPct = pct(counts.failed);
  const progPct = pct(counts.in_progress);
  const overallPct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="overflow-hidden rounded-lg border border-stone-200 bg-white">
      <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-4">
        <HeroStat
          label="Totalt"
          value={total}
          tone="muted"
        />
        <HeroStat
          label="Klara att skapa"
          value={counts.extracted}
          tone="ok"
        />
        <HeroStat
          label="Misslyckades"
          value={counts.failed}
          tone="warn"
        />
        <HeroStat
          label="Skapade"
          value={counts.confirmed}
          tone="info"
        />
      </div>
      <div className="space-y-1.5 border-t border-stone-100 px-4 py-3">
        <div className="flex items-center justify-between text-xs text-stone-600">
          <span>
            {done}/{total} klara
            {counts.in_progress > 0 && ` · ${counts.in_progress} pågår`}
          </span>
          <span className="font-medium text-stone-700">{overallPct}%</span>
        </div>
        <div className="flex h-2.5 w-full overflow-hidden rounded bg-stone-100">
          <div
            className="h-full bg-emerald-500 transition-all"
            style={{ width: `${okPct}%` }}
          />
          <div
            className="h-full bg-red-400 transition-all"
            style={{ width: `${failPct}%` }}
          />
          <div
            className="h-full bg-sky-300 transition-all"
            style={{ width: `${progPct}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function HeroStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'ok' | 'warn' | 'info' | 'muted';
}) {
  const toneCls =
    tone === 'ok'
      ? 'text-emerald-700'
      : tone === 'warn'
        ? 'text-red-700'
        : tone === 'info'
          ? 'text-stone-700'
          : 'text-stone-800';
  return (
    <div className="space-y-0.5">
      <div className={`text-2xl font-semibold ${toneCls}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-stone-500">{label}</div>
    </div>
  );
}

function ReadySection({
  items,
  templates,
  onAfterConfirm,
}: {
  items: ImportBatchItem[];
  templates: TemplateListRow[];
  onAfterConfirm: () => void | Promise<void>;
}) {
  const { token } = useTenantSession();
  const navigate = useNavigate();

  // Per-row template selection. Seeded from the extractor's guess; the
  // bulk-confirm bar can override all rows to the same template.
  //
  // Auto-seeding happens in an effect (not lazy useState init) because
  // both `items` and `templates` arrive asynchronously: the batch poll
  // can flip a row from `extracting` → `extracted` long after this
  // section first mounted, and `templates.list` may resolve after
  // `imports.batch_get`. A one-shot init would leave those rows stuck
  // on "Välj mall…" even though the extractor's guess is sitting right
  // there. The ref tracks which rows we've already seeded so the
  // effect doesn't clobber a deliberate user pick on later polls.
  const [perRow, setPerRow] = useState<Record<string, string | null>>({});
  const seededRef = useRef<Set<string>>(new Set());
  // Snapshot of `perRow` taken just before the first "apply to all"
  // override so the user can revert to whatever was there before
  // (AI guesses, or their own per-row picks). Cleared on revert and
  // not overwritten by subsequent applies — the revert goal is
  // "undo my bulk action", not "undo each step".
  const [preApplySnapshot, setPreApplySnapshot] = useState<
    Record<string, string | null> | null
  >(null);

  useEffect(() => {
    if (templates.length === 0) return;
    setPerRow((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const item of items) {
        if (seededRef.current.has(item.id)) continue;
        if (!item.templateTypeGuess) {
          // No guess to apply, but mark seeded so we don't re-check
          // every poll. If a guess shows up later (extractor finishes
          // mid-render), the row's id will already be in the set —
          // that's fine, the user can still pick manually.
          seededRef.current.add(item.id);
          continue;
        }
        const match = templates.find((t) => t.template_key === item.templateTypeGuess);
        if (!match) {
          // Templates loaded but this key isn't in the list (registry
          // mismatch, retired template). Don't lock it in — the user
          // can pick manually, and we'll keep trying on subsequent
          // template-list updates.
          continue;
        }
        seededRef.current.add(item.id);
        next[item.id] = match.version_id;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [items, templates]);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [bulkBusy, setBulkBusy] = useState(false);

  const readyToConfirmCount = items.filter((i) => perRow[i.id]).length;

  const confirmOne = async (item: ImportBatchItem) => {
    const versionId = perRow[item.id];
    if (!token || !versionId) return;
    setBusy((b) => ({ ...b, [item.id]: true }));
    setErrors((e) => ({ ...e, [item.id]: '' }));
    try {
      await tenantApi.imports.confirm({
        document_id: item.id,
        template_version_id: versionId,
      });
      await onAfterConfirm();
    } catch (e) {
      setErrors((er) => ({ ...er, [item.id]: (e as Error).message }));
    } finally {
      setBusy((b) => ({ ...b, [item.id]: false }));
    }
  };

  const confirmAll = async () => {
    if (!token) return;
    const targets = items.filter((i) => perRow[i.id]);
    if (targets.length === 0) return;
    setBulkBusy(true);
    // Small concurrency: confirm has DB work + party inserts; 3 at a
    // time keeps the backend honest without backpressure issues.
    const queue = [...targets];
    let cursor = 0;
    const workers: Promise<void>[] = [];
    const worker = async () => {
      while (cursor < queue.length) {
        const job = queue[cursor++];
        if (!job) break;
        await confirmOne(job);
      }
    };
    for (let w = 0; w < Math.min(3, queue.length); w++) workers.push(worker());
    await Promise.all(workers);
    setBulkBusy(false);
  };

  const applyTemplateToAll = (versionId: string) => {
    setPerRow((prev) => {
      if (preApplySnapshot === null) setPreApplySnapshot(prev);
      const next = { ...prev };
      for (const item of items) next[item.id] = versionId;
      return next;
    });
  };

  const revertApplyToAll = () => {
    if (preApplySnapshot === null) return;
    setPerRow(preApplySnapshot);
    setPreApplySnapshot(null);
  };

  return (
    <ResultsSection
      title="Klart att skapa"
      subtitle="Extraktionen är klar. Välj mall per avtal eller använd samma mall för alla."
      tone="ok"
      count={items.length}
      action={
        <div className="flex flex-wrap items-center gap-2">
          <ApplyToAllControl
            templates={templates}
            canRevert={preApplySnapshot !== null}
            onApply={applyTemplateToAll}
            onRevert={revertApplyToAll}
          />
          <button
            type="button"
            onClick={confirmAll}
            disabled={bulkBusy || readyToConfirmCount === 0}
            className="rounded bg-stone-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-stone-800 disabled:bg-stone-400"
          >
            {bulkBusy
              ? 'Skapar avtal…'
              : `Skapa alla (${readyToConfirmCount})`}
          </button>
        </div>
      }
    >
      <FileCardGrid>
        {items.map((item) => (
          <ReadyCard
            key={item.id}
            item={item}
            templates={templates}
            selectedVersion={perRow[item.id] ?? null}
            onSelect={(v) => setPerRow((prev) => ({ ...prev, [item.id]: v }))}
            onConfirm={() => confirmOne(item)}
            onReview={() => navigate(`/contracts/imports/${item.id}`)}
            busy={Boolean(busy[item.id]) || bulkBusy}
            error={errors[item.id] ?? null}
          />
        ))}
      </FileCardGrid>
    </ResultsSection>
  );
}

function ReadyCard({
  item,
  templates,
  selectedVersion,
  onSelect,
  onConfirm,
  onReview,
  busy,
  error,
}: {
  item: ImportBatchItem;
  templates: TemplateListRow[];
  selectedVersion: string | null;
  onSelect: (v: string | null) => void;
  onConfirm: () => void;
  onReview: () => void;
  busy: boolean;
  error: string | null;
}) {
  return (
    <FileCard
      filename={item.rawFilename}
      tag={
        item.templateTypeGuess && item.templateTypeGuess !== 'unknown'
          ? `AI: ${item.templateTypeGuess}`
          : null
      }
      tone="ok"
    >
      <div className="space-y-1.5">
        <TemplateSelect
          value={selectedVersion}
          templates={templates}
          onChange={onSelect}
        />
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={onReview}
            className="rounded border border-stone-300 bg-white px-2 py-1 text-[11px] hover:bg-stone-50"
          >
            Granska
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!selectedVersion || busy}
            className="rounded bg-stone-900 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-stone-800 disabled:bg-stone-400"
          >
            {busy ? 'Skapar…' : 'Skapa'}
          </button>
        </div>
        {error && <div className="text-[11px] text-red-700">{error}</div>}
      </div>
    </FileCard>
  );
}

function InProgressCard({ item }: { item: ImportBatchItem }) {
  return (
    <FileCard
      filename={item.rawFilename}
      tag={statusLabel(item.status)}
      tone="info"
    >
      <div className="flex items-center gap-2">
        <div className="h-1 flex-1 overflow-hidden rounded bg-stone-100">
          <div className="h-full w-1/2 animate-pulse bg-sky-400" />
        </div>
        <span className="text-[10px] text-stone-500">läser…</span>
      </div>
    </FileCard>
  );
}

/**
 * Failure section — `failed_extract` rows. Inline [Försök igen] + [Exkludera]
 * per card, plus a section-header bulk "Försök igen för alla (N)". Exclude
 * = soft-delete the row from this batch; the raw file stays in S3 until the
 * lifecycle retention clock kicks in (per ADR-0016).
 */
function FailedSection({
  items,
  onAfterChange,
}: {
  items: ImportBatchItem[];
  onAfterChange: () => void | Promise<void>;
}) {
  const { token } = useTenantSession();
  const [bulkBusy, setBulkBusy] = useState(false);
  const [busy, setBusy] = useState<Record<string, 'retry' | 'exclude' | null>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setRowBusy = (id: string, kind: 'retry' | 'exclude' | null) =>
    setBusy((b) => ({ ...b, [id]: kind }));

  const runRetry = async (item: ImportBatchItem) => {
    if (!token) return;
    setRowBusy(item.id, 'retry');
    setErrors((e) => ({ ...e, [item.id]: '' }));
    try {
      await tenantApi.imports.retry({ id: item.id });
      await onAfterChange();
    } catch (e) {
      setErrors((er) => ({ ...er, [item.id]: (e as Error).message }));
    } finally {
      setRowBusy(item.id, null);
    }
  };

  const runExclude = async (item: ImportBatchItem) => {
    if (!token) return;
    setRowBusy(item.id, 'exclude');
    setErrors((e) => ({ ...e, [item.id]: '' }));
    try {
      await tenantApi.imports.discard({ id: item.id });
      await onAfterChange();
    } catch (e) {
      setErrors((er) => ({ ...er, [item.id]: (e as Error).message }));
    } finally {
      setRowBusy(item.id, null);
    }
  };

  const retryAll = async () => {
    if (!token || bulkBusy) return;
    setBulkBusy(true);
    // Serial — keeps the backend honest and the per-row state pristine.
    for (const item of items) {
      try {
        await tenantApi.imports.retry({ id: item.id });
      } catch (e) {
        setErrors((er) => ({ ...er, [item.id]: (e as Error).message }));
      }
    }
    await onAfterChange();
    setBulkBusy(false);
  };

  return (
    <ResultsSection
      title="Misslyckades"
      subtitle="Filer som inte kunde läsas in. Försök igen om felet är tillfälligt, eller exkludera raden från den här importen."
      tone="error"
      count={items.length}
      action={
        items.length > 1 && (
          <button
            type="button"
            onClick={retryAll}
            disabled={bulkBusy}
            className="rounded border border-red-300 bg-white px-2.5 py-1.5 text-xs font-medium text-red-800 hover:bg-red-50 disabled:opacity-50"
          >
            {bulkBusy ? 'Försöker igen…' : `Försök igen för alla (${items.length})`}
          </button>
        )
      }
    >
      <FileCardGrid>
        {items.map((item) => (
          <FailedCard
            key={item.id}
            item={item}
            busy={busy[item.id] ?? null}
            error={errors[item.id] ?? null}
            onRetry={() => runRetry(item)}
            onExclude={() => runExclude(item)}
          />
        ))}
      </FileCardGrid>
    </ResultsSection>
  );
}

function FailedCard({
  item,
  busy,
  error,
  onRetry,
  onExclude,
}: {
  item: ImportBatchItem;
  busy: 'retry' | 'exclude' | null;
  error: string | null;
  onRetry: () => void;
  onExclude: () => void;
}) {
  return (
    <FileCard filename={item.rawFilename} tag={failedReasonLabel(item)} tone="error">
      <div className="flex items-center justify-end gap-1.5">
        <button
          type="button"
          onClick={onRetry}
          disabled={busy !== null}
          className="rounded border border-stone-300 bg-white px-2 py-1 text-[11px] hover:bg-stone-50 disabled:opacity-50"
        >
          {busy === 'retry' ? 'Försöker…' : 'Försök igen'}
        </button>
        <button
          type="button"
          onClick={onExclude}
          disabled={busy !== null}
          className="rounded border border-stone-300 bg-white px-2 py-1 text-[11px] text-stone-600 hover:bg-stone-50 disabled:opacity-50"
          title="Tar bort den här raden från importen. Inget avtal skapas. Andra rader påverkas inte."
        >
          {busy === 'exclude' ? 'Exkluderar…' : 'Exkludera'}
        </button>
      </div>
      {error && <div className="mt-1.5 text-[11px] text-red-700">{error}</div>}
    </FileCard>
  );
}

/**
 * Scanned-image section — non-retryable. The extractor classified the file
 * as image-based PDF; running the same bytes again won't change that.
 * Only [Exkludera] is offered.
 */
function OcrRequiredSection({
  items,
  onAfterChange,
}: {
  items: ImportBatchItem[];
  onAfterChange: () => void | Promise<void>;
}) {
  const { token } = useTenantSession();
  const [busyId, setBusyId] = useState<string | null>(null);

  const exclude = async (item: ImportBatchItem) => {
    if (!token) return;
    setBusyId(item.id);
    try {
      await tenantApi.imports.discard({ id: item.id });
      await onAfterChange();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <ResultsSection
      title="Skannade bilder"
      subtitle="Bildbaserade PDF:er — vi kan inte läsa text från dem. Exkludera och ladda upp en text-baserad fil, eller skapa avtalet från en mall."
      tone="warn"
      count={items.length}
    >
      <FileCardGrid>
        {items.map((item) => (
          <FileCard
            key={item.id}
            filename={item.rawFilename}
            tag="Skannad bild"
            tone="warn"
          >
            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={() => exclude(item)}
                disabled={busyId === item.id}
                className="rounded border border-stone-300 bg-white px-2 py-1 text-[11px] text-stone-600 hover:bg-stone-50 disabled:opacity-50"
              >
                {busyId === item.id ? 'Exkluderar…' : 'Exkludera'}
              </button>
            </div>
          </FileCard>
        ))}
      </FileCardGrid>
    </ResultsSection>
  );
}

/**
 * Red banner above the sections when 100 % of the batch errored — gives
 * a one-click bulk retry + discard for the "extractor is down" case
 * (e.g. AWS creds missing in dev). The same actions exist further down
 * the page, but the banner makes them impossible to miss when there's
 * nothing else useful on screen.
 */
function AllFailedBanner({
  batchId,
  failedCount,
  onAfterChange,
  onDiscarded,
}: {
  batchId: string;
  failedCount: number;
  onAfterChange: () => void | Promise<void>;
  onDiscarded: () => void;
}) {
  const { token } = useTenantSession();
  const [busy, setBusy] = useState<'retry' | 'discard' | null>(null);

  const retryAll = async () => {
    if (!token || busy) return;
    setBusy('retry');
    try {
      // Walk the IDs server-side via a fresh detail fetch each time would
      // be slow; this banner only fires when every item is failed, so
      // we trust the count and refetch state once the loop is done.
      const fresh = await tenantApi.imports.batch_get(batchId);
      for (const it of fresh.items) {
        if (it.status === 'failed_extract') {
          try {
            await tenantApi.imports.retry({ id: it.id });
          } catch {
            // per-row error surfaces in the row card after refresh
          }
        }
      }
      await onAfterChange();
    } finally {
      setBusy(null);
    }
  };

  const discardBatch = async () => {
    if (!token || busy) return;
    if (!confirm('Kasta hela importen? Alla ej skapade rader exkluderas.')) return;
    setBusy('discard');
    try {
      await tenantApi.imports.batch_discard({ id: batchId });
      onDiscarded();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
      <div className="flex flex-wrap items-start gap-3">
        <div className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
          !
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-red-900">
            Alla {failedCount} filer misslyckades
          </div>
          <div className="mt-0.5 text-[11px] text-red-900/80">
            Extraktionstjänsten kunde inte nås. Det är troligen tillfälligt —
            kör <em>Försök igen för alla</em> om en stund, eller kasta hela
            importen om du vill börja om.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={retryAll}
            disabled={busy !== null}
            className="rounded bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 disabled:opacity-50"
          >
            {busy === 'retry' ? 'Försöker igen…' : `Försök igen för alla (${failedCount})`}
          </button>
          <button
            type="button"
            onClick={discardBatch}
            disabled={busy !== null}
            className="rounded border border-red-300 bg-white px-3 py-1.5 text-xs text-red-800 hover:bg-red-100 disabled:opacity-50"
          >
            {busy === 'discard' ? 'Kastar…' : 'Kasta hela importen'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Footer toolbar — two ways out of the batch view:
 *   - "Färdig – avsluta import"  → navigate away. Enabled only once at
 *                                   least one contract has been created
 *                                   (otherwise the user would leave with
 *                                   nothing to show). Any rows still
 *                                   pending stay around for next time;
 *                                   the batch is non-destructive.
 *   - "Kasta hela importen"      → batch_discard: soft-delete the batch +
 *                                   every non-confirmed member row.
 *                                   Confirmed rows (= already-created
 *                                   contracts) are kept.
 */
function BatchFooterActions({
  batchId,
  confirmedCount,
  pendingCount,
  onDone,
}: {
  batchId: string;
  confirmedCount: number;
  pendingCount: number;
  onDone: () => void;
}) {
  const { token } = useTenantSession();
  const [busy, setBusy] = useState(false);
  const canFinish = confirmedCount > 0;

  const discard = async () => {
    if (!token || busy) return;
    const msg =
      pendingCount > 0
        ? `Kasta hela importen? ${pendingCount} ej skapade rader exkluderas. ${confirmedCount} redan skapade avtal påverkas inte.`
        : 'Kasta hela importen?';
    if (!confirm(msg)) return;
    setBusy(true);
    try {
      await tenantApi.imports.batch_discard({ id: batchId });
      onDone();
    } finally {
      setBusy(false);
    }
  };

  const finishHint = canFinish
    ? pendingCount > 0
      ? `Avslutar vyn för den här importen. ${pendingCount} ej hanterade rader sparas — du kan återvända till importen senare. ${confirmedCount} skapade avtal påverkas inte.`
      : 'Avslutar vyn för den här importen. Alla skapade avtal påverkas inte.'
    : 'Skapa minst ett avtal innan du kan avsluta importen. Använd "Kasta hela importen" om du vill stänga utan att skapa något.';

  return (
    <div className="space-y-1.5 rounded border border-stone-200 bg-stone-50 px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-stone-500">
          {confirmedCount} skapade
          {pendingCount > 0 ? ` · ${pendingCount} ej hanterade` : ' · alla hanterade'}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={discard}
            disabled={busy}
            className="rounded border border-stone-300 bg-white px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-50 disabled:opacity-50"
          >
            {busy ? 'Kastar…' : 'Kasta hela importen'}
          </button>
          <button
            type="button"
            onClick={onDone}
            disabled={!canFinish}
            title={finishHint}
            className="rounded bg-stone-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-400"
          >
            Färdig – avsluta import
          </button>
        </div>
      </div>
      <p className="text-[11px] text-stone-500">{finishHint}</p>
    </div>
  );
}

function ConfirmedCard({ item }: { item: ImportBatchItem }) {
  return (
    <FileCard filename={item.rawFilename} tag="Avtal skapat" tone="muted">
      <div className="space-y-2">
        {item.contractId && <MonitoringSummary contractId={item.contractId} />}
        <div className="flex items-center justify-end">
          {item.contractId && (
            <Link
              to={`/contracts/${item.contractId}/edit`}
              className="rounded border border-stone-300 bg-white px-2.5 py-1 text-[11px] font-medium text-stone-700 hover:bg-stone-50"
            >
              Öppna avtal →
            </Link>
          )}
        </div>
      </div>
    </FileCard>
  );
}

/**
 * Per-card monitoring strip on a created import. Shows the count of
 * template-derived reminders that landed when the contract was created
 * (excluding user-added custom reminders), the upcoming one's date +
 * label, and an inline toggle to turn the schedule off / back on.
 *
 * "Off" hard-deletes the materialized rows on the server. "On"
 * re-materializes from the template against the contract's current
 * values — same logic as the import-confirm path.
 */
function MonitoringSummary({ contractId }: { contractId: string }) {
  const { token } = useTenantSession();
  const invalidate = useInvalidate();
  const q = useTenantQuery(
    'notifications.list_by_contract',
    (id) => tenantApi.notifications.list_by_contract({ contract_id: id as string }),
    [contractId],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const all = q.data?.notifications ?? [];
  const templateRows = all.filter((n) => n.kind !== 'custom');
  const upcoming = pickPrimaryReminder(templateRows);
  const isOn = templateRows.length > 0;

  const refresh = async () => {
    await q.mutate();
    await invalidate('notifications.list_by_contract');
    await invalidate('notifications.list_due');
    await invalidate('notifications.list_upcoming');
    await invalidate('notifications.list_grouped_by_contract');
  };

  const toggle = async () => {
    if (!token || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (isOn) {
        await tenantApi.notifications.disable_for_contract({
          contract_id: contractId,
        });
      } else {
        await tenantApi.notifications.enable_for_contract({
          contract_id: contractId,
        });
      }
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (q.isLoading) {
    return (
      <div className="text-[11px] text-stone-400">Läser bevakningar…</div>
    );
  }

  return (
    <div className="rounded border border-stone-200 bg-stone-50 px-2 py-1.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 text-[11px] leading-snug text-stone-700">
          {isOn ? (
            <>
              <div className="font-medium text-stone-800">
                {scheduledCountSentence(templateRows.length)}
              </div>
              {upcoming && (
                <div className="truncate text-stone-500" title={reminderTitle(upcoming)}>
                  Den första är{' '}
                  <span className="text-stone-700">
                    {reminderTitle(upcoming)}
                  </span>{' '}
                  · {formatReminderDate(upcoming.fireAt)}
                </div>
              )}
            </>
          ) : (
            <div className="text-stone-500">
              Ingen bevakning aktiv för detta avtal.
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={busy}
          title={
            isOn
              ? 'Stäng av schemalagda påminnelser för detta avtal'
              : 'Slå på påminnelser igen — räknar om från mallens regler'
          }
          className="inline-flex shrink-0 items-center gap-1 rounded border border-stone-300 bg-white px-2 py-0.5 text-[11px] text-stone-700 hover:bg-stone-50 disabled:opacity-50"
        >
          {isOn ? (
            <>
              <BellOff className="h-3 w-3" />
              {busy ? 'Stänger av…' : 'Stäng av'}
            </>
          ) : (
            <>
              <BellRing className="h-3 w-3" />
              {busy ? 'Slår på…' : 'Slå på'}
            </>
          )}
        </button>
      </div>
      {error && <div className="mt-1 text-[11px] text-red-700">{error}</div>}
    </div>
  );
}

const REMINDER_KIND_LABELS: Record<NotificationKind, string> = {
  termination_notice: 'Uppsägning',
  negotiation_window: 'Förhandlingsfönster',
  lease_end: 'Hyresavtal löper ut',
  agreement_end: 'Förhandlingsordning löper ut',
  signature_overdue: 'Inväntar signering',
  custom: 'Påminnelse',
};

function scheduledCountSentence(n: number): string {
  if (n === 1) return '1 bevakning tillagd';
  return `${n} bevakningar tillagda`;
}

/**
 * Pick the row that best represents the schedule on the card: the next
 * un-acknowledged reminder in the future, falling back to the next
 * un-acknowledged regardless of fire date, then to the earliest row at
 * all. Keeps the card useful even for contracts where every reminder
 * is already in the past (rare on a fresh import, but possible).
 */
function pickPrimaryReminder(
  rows: ContractNotification[],
): ContractNotification | null {
  if (rows.length === 0) return null;
  const now = Date.now();
  const future = rows
    .filter((r) => !r.acknowledgedAt && new Date(r.fireAt).getTime() > now)
    .sort((a, b) => a.fireAt.localeCompare(b.fireAt));
  if (future[0]) return future[0];
  const unack = rows
    .filter((r) => !r.acknowledgedAt)
    .sort((a, b) => a.fireAt.localeCompare(b.fireAt));
  if (unack[0]) return unack[0];
  return [...rows].sort((a, b) => a.fireAt.localeCompare(b.fireAt))[0] ?? null;
}

function reminderTitle(n: ContractNotification): string {
  const m = n.meta as { label?: unknown };
  if (typeof m.label === 'string' && m.label) return m.label;
  return REMINDER_KIND_LABELS[n.kind];
}

function formatReminderDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 10);
}

function FileCard({
  filename,
  tag,
  tone,
  children,
}: {
  filename: string;
  tag: string | null;
  tone: 'ok' | 'warn' | 'info' | 'muted' | 'error';
  children: React.ReactNode;
}) {
  const accent =
    tone === 'ok'
      ? 'border-l-emerald-400'
      : tone === 'warn'
        ? 'border-l-amber-400'
        : tone === 'error'
          ? 'border-l-red-400'
          : tone === 'info'
            ? 'border-l-sky-400'
            : 'border-l-stone-300';
  const mime = filename.toLowerCase().endsWith('.pdf')
    ? 'application/pdf'
    : filename.toLowerCase().endsWith('.docx')
      ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      : null;
  return (
    <div
      className={`flex flex-col gap-2 rounded border border-stone-200 border-l-4 bg-white p-3 shadow-sm ${accent}`}
    >
      <div className="flex items-start gap-2.5">
        <FileTypeIcon mime={mime} size="md" />
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="truncate text-sm font-medium text-stone-800" title={filename}>
            {filename}
          </div>
          {tag && (
            <div className="truncate text-[10px] uppercase tracking-wider text-stone-500">
              {tag}
            </div>
          )}
        </div>
      </div>
      <div className="pt-1">{children}</div>
    </div>
  );
}

function FileCardGrid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 lg:grid-cols-3">
      {children}
    </div>
  );
}

function ResultsSection({
  title,
  subtitle,
  tone,
  count,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  tone: 'ok' | 'warn' | 'info' | 'muted' | 'error';
  count?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const headerTone =
    tone === 'ok'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
      : tone === 'warn'
        ? 'border-amber-200 bg-amber-50 text-amber-900'
        : tone === 'error'
          ? 'border-red-200 bg-red-50 text-red-900'
          : tone === 'info'
            ? 'border-sky-200 bg-sky-50 text-sky-900'
            : 'border-stone-200 bg-stone-50 text-stone-700';
  return (
    <div className="overflow-hidden rounded-lg border border-stone-200 bg-white">
      <div className={`flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2 ${headerTone}`}>
        <div className="space-y-0.5">
          <div className="text-sm font-medium">
            {title}
            {typeof count === 'number' && (
              <span className="ml-1.5 opacity-70">({count})</span>
            )}
          </div>
          {subtitle && <div className="text-[11px] opacity-80">{subtitle}</div>}
        </div>
        {action && <div>{action}</div>}
      </div>
      {children}
    </div>
  );
}

/**
 * "Apply template to all rows" trigger for the bulk action bar.
 *
 * Renders as a labelled select that explicitly says what it does
 * ("Tillämpa på alla…") rather than showing a template name as if
 * one were already pre-selected. After a pick is committed the
 * select resets to its blank label — the active state lives in the
 * per-row dropdowns + the revert button, not in this control.
 *
 * The revert button only appears once an apply-to-all has actually
 * happened. Pressing it restores whatever was in each row before
 * the first apply (typically the AI guesses).
 */
function ApplyToAllControl({
  templates,
  canRevert,
  onApply,
  onRevert,
}: {
  templates: TemplateListRow[];
  canRevert: boolean;
  onApply: (versionId: string) => void;
  onRevert: () => void;
}) {
  // Local state seeded to "" so the <select> always shows the action
  // label, never an apparent default. Resets to "" after each commit.
  const [draft, setDraft] = useState('');
  return (
    <div className="flex items-center gap-2">
      <label className="text-[11px] text-stone-500">Tillämpa på alla:</label>
      <select
        value={draft}
        onChange={(e) => {
          const v = e.target.value;
          if (!v) return;
          setDraft('');
          onApply(v);
        }}
        className="rounded border border-stone-300 bg-white px-2 py-1 text-[11px]"
      >
        <option value="">Välj mall…</option>
        {templates.map((t) => {
          const short =
            getTemplateVersion(t.template_key, t.version)?.metadata.shortTitle ?? t.name;
          return (
            <option key={t.version_id} value={t.version_id}>
              {short}
            </option>
          );
        })}
      </select>
      {canRevert && (
        <button
          type="button"
          onClick={onRevert}
          title="Återställ varje rads tidigare val (AI-gissningar eller dina egna val)."
          className="rounded border border-stone-300 bg-white px-2 py-1 text-[11px] text-stone-700 hover:bg-stone-50"
        >
          Återställ
        </button>
      )}
    </div>
  );
}

function TemplateSelect({
  value,
  templates,
  onChange,
  placeholder = 'Välj mall…',
  compact = false,
}: {
  value: string | null;
  templates: TemplateListRow[];
  onChange: (v: string | null) => void;
  placeholder?: string;
  compact?: boolean;
}) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      className={`w-full rounded border border-stone-300 bg-white px-2 py-1 ${
        compact ? 'text-[11px]' : 'text-xs'
      }`}
    >
      <option value="">{placeholder}</option>
      {templates.map((t) => {
        const short =
          getTemplateVersion(t.template_key, t.version)?.metadata.shortTitle ?? t.name;
        return (
          <option key={t.version_id} value={t.version_id}>
            {short}
          </option>
        );
      })}
    </select>
  );
}

function isReadyToConfirm(item: ImportBatchItem): boolean {
  return item.status === 'extracted';
}

function isInProgress(item: ImportBatchItem): boolean {
  return (
    item.status === 'uploaded' ||
    item.status === 'queued' ||
    item.status === 'extracting'
  );
}

function isFailedExtract(item: ImportBatchItem): boolean {
  return item.status === 'failed_extract';
}

/**
 * Per-row label shown on a failed card. Maps the structured
 * `unsupported_template_type` sentinel to a friendly Swedish string,
 * otherwise falls back to the raw `errorMessage` (already user-safe per
 * the doc-parser's failure paths).
 */
function failedReasonLabel(item: ImportBatchItem): string {
  if (item.errorMessage === 'unsupported_template_type')
    return 'Dokumenttypen stöds inte';
  if (item.errorMessage) return item.errorMessage;
  return 'Tekniskt fel — försök igen';
}

function statusLabel(s: ImportStatus): string {
  switch (s) {
    case 'uploaded':
    case 'queued':
      return 'I kö';
    case 'extracting':
      return 'Läser dokumentet…';
    case 'extracted':
      return 'Klar för granskning';
    case 'failed_ocr_required':
      return 'Skannad bild';
    case 'failed_extract':
      return 'Misslyckad extraktion';
    case 'confirmed':
      return 'Avtal skapat';
  }
}

function countByStatus(detail: ImportBatchDetail) {
  let extracted = 0;
  let failed = 0;
  let confirmed = 0;
  let inProgress = 0;
  for (const i of detail.items) {
    if (i.status === 'extracted') extracted++;
    else if (i.status === 'confirmed') confirmed++;
    else if (i.status === 'failed_extract' || i.status === 'failed_ocr_required')
      failed++;
    else inProgress++;
  }
  // Files the browser hasn't even registered yet count toward in-progress
  // for the denominator's sake.
  const missing = Math.max(0, detail.total_count - detail.items.length);
  return { extracted, failed, confirmed, in_progress: inProgress + missing };
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('sv-SE');
  } catch {
    return iso;
  }
}
